// Tracker charts: a time axis that adapts to the range, a range switch, and two charts.
//   TrackerChart     the list page: each system's completes against its target.
//   QuotaPairChart   the detail page: one quota's fill in each system against that system's overall.
// Systems keep their own colour (SYSTEM_COLOURS); where both draw, Alchemer is a thick solid line
// and Wrangler a dashed one on top, so overlapping lines stay distinguishable. Targets and
// overalls are labelled on the chart, not in the legend.
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, TimeScale, Tooltip,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';
import { format } from 'date-fns';
import { RANGES, rangeBounds, timeScale, ts } from './timeAxis';
import {
  snapshotOverall, snapshotQuotaPct, snapshotTotalCompletes, SYSTEM_COLOURS, SYSTEM_LABELS, targetN,
} from './trackerSeries';

ChartJS.register(LinearScale, LineElement, PointElement, TimeScale, Tooltip, Legend, annotationPlugin);

const HOUR = 3600 * 1000;

export const RangeSwitch = ({ value, onChange }) => (
  <ToggleButtonGroup size="small" exclusive value={value} onChange={(e, v) => v && onChange(v)} aria-label="Chart range">
    {RANGES.map((r) => (
      <ToggleButton key={r.id} value={r.id} sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}>{r.label}</ToggleButton>
    ))}
  </ToggleButtonGroup>
);

const withAlpha = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

/** The points that draw inside the range: from one hour before its start, so lines reach the edge. */
const clip = (snapshots, bounds) => (snapshots || []).filter((s) => ts(s) >= bounds.min - HOUR && ts(s) <= bounds.max);

const lineStyle = (system, mixed) => ({
  borderColor: SYSTEM_COLOURS[system],
  backgroundColor: SYSTEM_COLOURS[system],
  borderWidth: mixed && system === 'alchemer' ? 3.5 : 2,
  borderDash: mixed && system === 'wrangler' ? [7, 4] : [],
  order: system === 'wrangler' ? 0 : 1, // drawn on top
  tension: 0.2,
  pointHoverRadius: 4,
});

/** A label at the right end of a line, nudged up or down so two labels don't collide. */
const endLabel = (point, text, colour, nudge) => ({
  type: 'label',
  xValue: point.x,
  yValue: point.y,
  content: text,
  position: { x: 'end', y: 'center' },
  xAdjust: -6,
  yAdjust: nudge,
  color: colour,
  backgroundColor: 'rgba(255,255,255,0.85)',
  padding: 3,
  font: { size: 11, weight: '600' },
});

const baseOptions = (bounds, yAxis, annotations) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  interaction: { mode: 'nearest', axis: 'x', intersect: false },
  layout: { padding: { top: 14, right: 8 } }, // room for labels at the top edge
  plugins: {
    legend: {
      position: 'top',
      align: 'start',
      labels: {
        boxWidth: 28,
        boxHeight: 2,
        font: { size: 12 },
        filter: (item, data) => !data.datasets[item.datasetIndex].unlabelled,
      },
    },
    annotation: { clip: false, annotations }, // labels may sit just outside the plot
  },
  scales: { x: timeScale(bounds), y: yAxis },
});

/** "1 Alchemer snapshot so far (14:07). The next is due around 15:07." */
export const SnapshotNote = ({ snapshots }) => {
  if (!snapshots || snapshots.length !== 1) return null;
  const at = ts(snapshots[0]);
  return (
    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
      Only one Alchemer snapshot so far ({format(at, 'HH:mm')}). Snapshots are taken hourly: the next is due
      around {format(at + HOUR, 'HH:mm')}.
    </Typography>
  );
};

/** The list page's chart: each system's completes against its target (stepped where limits rose). */
export const TrackerChart = ({ series, rangeId, height = 280 }) => {
  const systems = ['alchemer', 'wrangler'].filter((s) => series[s]?.length);
  const bounds = rangeBounds(systems.map((s) => series[s]), rangeId);
  if (!bounds) return <Typography variant="body2" color="text.secondary">No data yet.</Typography>;
  const mixed = systems.length > 1;

  const datasets = [];
  const ends = [];
  for (const system of systems) {
    const points = clip(series[system], bounds);
    datasets.push({
      label: `${SYSTEM_LABELS[system]} completes`,
      data: points.map((s) => ({ x: ts(s), y: snapshotTotalCompletes(s) })),
      ...lineStyle(system, mixed),
      pointRadius: points.length === 1 ? 4 : 0,
    });
    const targets = points.map((s) => ({ x: ts(s), y: targetN(s.data?.quotas || []) }));
    datasets.push({
      label: `${SYSTEM_LABELS[system]} target`,
      unlabelled: true,
      data: targets,
      borderColor: withAlpha(SYSTEM_COLOURS[system], 0.55),
      borderWidth: 1.5,
      borderDash: [2, 3],
      pointRadius: points.length === 1 ? 3 : 0,
      stepped: true, // a limit increase is a step, at the point it was first seen
      order: 2,
    });
    if (targets.length) ends.push({ system, point: targets[targets.length - 1] });
  }

  const annotations = {};
  if (ends.length === 2 && ends[0].point.y === ends[1].point.y) {
    annotations.target = endLabel(ends[0].point, `Target ${ends[0].point.y.toLocaleString()} (both)`, '#555', -11);
  } else {
    ends.forEach(({ system, point }, n) => {
      const name = mixed ? `${SYSTEM_LABELS[system]} target` : 'Target';
      annotations[`target-${system}`] = endLabel(point, `${name} ${point.y.toLocaleString()}`, SYSTEM_COLOURS[system], n ? 11 : -11);
    });
  }

  const yAxis = { beginAtZero: true, grace: '8%', title: { display: true, text: 'Completes' }, grid: { color: '#eef0f3' } };
  return (
    <>
      <Box sx={{ height }}>
        <Line data={{ datasets }} options={baseOptions(bounds, yAxis, annotations)} />
      </Box>
      <SnapshotNote snapshots={series.alchemer} />
    </>
  );
};

/**
 * The detail page's chart for one quota (both systems' rows of it): its fill against its system's
 * overall fill, with a marker wherever its limit changed.
 */
export const QuotaPairChart = ({ rows, series, rangeId, height = 240 }) => {
  const present = rows.filter((q) => series[q.system]?.length);
  const bounds = rangeBounds(present.map((q) => series[q.system]), rangeId);
  if (!bounds) return <Typography variant="body2" color="text.secondary">No data yet.</Typography>;
  const mixed = present.length > 1;

  const datasets = [];
  const annotations = {
    full: {
      type: 'line', yMin: 100, yMax: 100, borderColor: 'rgba(0,0,0,0.25)', borderWidth: 1,
      label: { display: true, content: 'Limit', position: 'start', backgroundColor: 'transparent', color: '#777', font: { size: 10 }, yAdjust: -8 },
    },
  };
  present.forEach((quota, n) => {
    const { system } = quota;
    const colour = SYSTEM_COLOURS[system];
    const points = clip(series[system], bounds);
    datasets.push({
      label: mixed ? `${SYSTEM_LABELS[system]}` : 'This quota',
      data: points.map((s) => ({ x: ts(s), y: snapshotQuotaPct(s, quota.id) })),
      ...lineStyle(system, mixed),
      pointRadius: points.length === 1 ? 4 : 0,
    });
    const overall = points.map((s) => ({ x: ts(s), y: snapshotOverall(s) }));
    datasets.push({
      label: `${SYSTEM_LABELS[system]} overall`,
      unlabelled: true,
      data: overall,
      borderColor: withAlpha(colour, 0.5),
      borderWidth: 1.5,
      borderDash: [2, 3],
      pointRadius: 0,
      order: 2,
    });
    const end = overall[overall.length - 1];
    if (end) {
      const name = mixed ? `${SYSTEM_LABELS[system]} overall` : 'Overall';
      annotations[`overall-${system}`] = endLabel(end, `${name} ${end.y.toFixed(1)}%`, colour, n ? 11 : -11);
    }
    // Limit changes: a marker where the limit differs from the point before.
    let previous = null;
    points.forEach((s) => {
      const limit = (s.data?.quotas || []).find((q) => String(q.id) === String(quota.id))?.limit;
      if (previous != null && limit != null && Number(limit) !== Number(previous) && ts(s) >= bounds.min) {
        annotations[`limit-${system}-${ts(s)}`] = {
          type: 'line', xMin: ts(s), xMax: ts(s), borderColor: withAlpha(colour, 0.6), borderWidth: 1, borderDash: [4, 4],
          label: {
            display: true,
            content: `${mixed ? `${SYSTEM_LABELS[system]} limit` : 'Limit'} ${previous} → ${limit}`,
            position: n ? 'end' : 'start',
            backgroundColor: withAlpha(colour, 0.9),
            font: { size: 10, weight: '600' },
            padding: 3,
          },
        };
      }
      if (limit != null) previous = limit;
    });
  });

  const yAxis = {
    min: 0, suggestedMax: 100, grace: '5%',
    title: { display: true, text: 'Fill' },
    ticks: { callback: (v) => `${v}%` },
    grid: { color: '#eef0f3' },
  };
  return (
    <>
      <Box sx={{ height }}>
        <Line data={{ datasets }} options={baseOptions(bounds, yAxis, annotations)} />
      </Box>
      {rows.some((q) => q.system === 'alchemer') && <SnapshotNote snapshots={series.alchemer} />}
    </>
  );
};
